/* eslint-disable fp/no-mutation */
import { ReactElement, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import LiveTvIcon from '@mui/icons-material/LiveTv'
import Grid from '@mui/material/Grid'

import theme from 'src/theme'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useParams } from 'react-router-dom'
import {
  CancelIcon,
  EditIcon,
  ImageIcon,
  SubmitIcon,
  UploadFileIcon,
} from 'src/v3/core/icons'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios, { pluckData } from 'src/utilities/http-client'
import Subheader from 'src/v3/components/subheader'
import {
  ContentType,
  contentTypes,
  FileData,
} from 'src/v3/utilities/filesystem'
import TitleScreenView from './title-screen'
import Quiz from './quiz'
import Accreditation from './accreditation'
import {
  fetchLiveClasses,
  selectLiveClassById,
  selectLiveClassesStatus,
  fetchLiveClassTemplates,
  selectLiveClassTemplates,
  selectLiveClassTemplatesStatus,
  fetchLiveClassCourses,
  selectLiveClassCourseByCode,
  selectLiveClassCoursesStatus,
} from '../admin.slice'

dayjs.extend(utc)

let updateLiveClassImage = (data: { id: string; imageType: string }) =>
  axios.put<string>('/api/v2/admin/live-class/image', data).then(pluckData)

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let { liveClassID = '' } = useParams()

  let liveClassesStatus = useAppSelector(selectLiveClassesStatus)
  let selectedLiveClass = useAppSelector(selectLiveClassById(liveClassID))
  let templates = useAppSelector(selectLiveClassTemplates)
  let templatesStatus = useAppSelector(selectLiveClassTemplatesStatus)
  let course = useAppSelector(
    selectLiveClassCourseByCode(selectedLiveClass?.courseCode || ''),
  )
  let coursesStatus = useAppSelector(selectLiveClassCoursesStatus)

  let [formOpen, setFormOpen] = useState<string>('')
  let [fileToUpload, setFileToUpload] = useState<FileData | undefined>(
    undefined,
  )
  let [existingFile, setExistingFile] = useState<string | undefined>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      meetingUrl: yup.string().required(),
      quizPasscode: yup.string().required(),
      code: yup.string().required(),
      hours: yup.number().not([0]).required(),
      section: yup.string().required(),
      templateID: yup.string().not(['unselected']).required(),
    }),
    enableReinitialize: true,
    initialValues: {
      name: '',
      meetingUrl: '',
      quizPasscode: '',
      code: '',
      hours: 0,
      section: '',
      templateID: 'unselected',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/v2/admin/live-class/course', {
          id: course?.id || '',
          code: values.code,
          courseName: values.name,
          courseLength: values.hours,
          templateID: values.templateID,
        })
        .then(() =>
          axios.put('/api/v2/admin/live-class', {
            id: liveClassID,
            name: values.name,
            meetingUrl: values.meetingUrl,
            quizPasscode: values.quizPasscode,
            courseCode: values.code,
          }),
        )
        .then(() =>
          axios.put('/api/admin/live-class/section', {
            id: liveClassID,
            section: values.section,
          }),
        )
        .then(pluckData)
        .then(() => {
          if (fileToUpload) {
            updateLiveClassImage({
              id: liveClassID,
              imageType: fileToUpload.extension === 'jpeg' ? 'jpg' : 'png',
            }).then((response) =>
              axios.put(response, fileToUpload?.file, {
                headers: {
                  'Cache-Control': 'max-age=2592000',
                  'content-type': fileToUpload?.type,
                },
              }),
            )
          }
        })
        .then(() => dispatch(fetchLiveClassCourses()))
        .then(() => dispatch(fetchLiveClasses()))
        .then(() => setFormOpen(''))
        .then(() => setSaving(false))
        .then(() => form.resetForm())
        .catch((err) => {
          setErrorMessage(
            err?.response?.data || err?.message || 'Error Updating Live Class',
          )
          dispatch(fetchLiveClassCourses())
          dispatch(fetchLiveClasses())
          setFormOpen('')
          setSaving(false)
        })
    },
  })

  let handleCapture = (event: React.ChangeEvent) => {
    let file = (event.target as HTMLInputElement).files?.[0]
    let fileName = file?.name || ''
    let reader = new FileReader()

    reader.onloadend = () => {
      let fileData = reader.result
        ?.toString()
        ?.match(/data:[a-z]+\/[a-z]+/)?.[0]
      if (!fileData) {
        setErrorMessage('Live Class Image Upload Failed: Read File Failed')
        return
      }

      let fileType = fileData.split(':')[1]
      let fileExtension = fileType.split('/')[1]

      if (!contentTypes.includes(fileType as ContentType)) {
        setErrorMessage(`Invalid File Type ${fileType}`)
        return
      }

      let binary = atob(reader.result?.toString().split(',')[1] as string)
      let array = new Array(binary.length)
        .fill('')
        .map((_, i) => binary.charCodeAt(i))
      let blobData = new Blob([new Uint8Array(array)], { type: fileExtension })

      setFileToUpload({
        name: fileName,
        type: fileType,
        extension: fileExtension,
        file: blobData,
      })
    }

    reader.readAsDataURL(file!)
  }

  let onFormOpen = () => {
    form.setFieldValue('name', selectedLiveClass?.name || '')
    form.setFieldValue('meetingUrl', selectedLiveClass?.meetingUrl || '')
    form.setFieldValue('quizPasscode', selectedLiveClass?.quizPasscode || '')
    form.setFieldValue('code', selectedLiveClass?.courseCode || '')
    form.setFieldValue('section', selectedLiveClass?.section || '')
    form.setFieldValue('hours', course?.courseLength || 0)
    form.setFieldValue('templateID', course?.templateID || 'unselected')
    setExistingFile(selectedLiveClass?.imageName)
    setFormOpen('live-class')
  }

  useEffect(() => {
    if (liveClassesStatus === 'uninitialized') {
      dispatch(fetchLiveClasses())
    }
  }, [liveClassesStatus])

  useEffect(() => {
    if (templatesStatus === 'uninitialized') {
      dispatch(fetchLiveClassTemplates())
    }
  }, [templatesStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchLiveClassCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (!formOpen) {
      setExistingFile('')
      setFileToUpload(undefined)
      form.resetForm()
    }
  }, [formOpen])

  return (
    <>
      <div className="flex items-center justify-center mt-2 mb-4">
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          <LiveTvIcon />
        </Avatar>
        <Typography
          variant="h4"
          className="pl-2"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          {selectedLiveClass?.name}
        </Typography>
      </div>
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {selectedLiveClass && (
            <>
              <Grid lg={6} sm={12}>
                <Box sx={{ mt: '-30px' }}>
                  <Subheader title="Summary" />
                </Box>
                <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                  <Button
                    startIcon={<EditIcon />}
                    variant="outlined"
                    onClick={onFormOpen}
                  >
                    Edit Live Class
                  </Button>
                </Box>
                <Card variant="elevation">
                  <CardMedia
                    sx={{ height: 362 }}
                    image={selectedLiveClass?.imageName}
                    title={selectedLiveClass.name}
                  />
                  <CardContent
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <Typography gutterBottom variant="h5" component="div">
                      {selectedLiveClass.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Meeting URL: {selectedLiveClass.meetingUrl}
                    </Typography>
                    {selectedLiveClass.courseCode && (
                      <Typography variant="body2" color="text.secondary">
                        Course Code: {selectedLiveClass.courseCode}
                      </Typography>
                    )}
                    {selectedLiveClass.section && (
                      <Typography variant="body2" color="text.secondary">
                        Section: {selectedLiveClass.section}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid lg={6} sm={12}>
                <TitleScreenView liveClass={selectedLiveClass} />
              </Grid>
              <Grid lg={6} sm={12}>
                <Quiz liveClass={selectedLiveClass} />
              </Grid>
              <Grid lg={6} sm={12}>
                <Accreditation liveClass={selectedLiveClass} />
              </Grid>
            </>
          )}
        </Grid>
      </Container>
      <Dialog open={formOpen === 'live-class'} onClose={() => setFormOpen('')}>
        <form onSubmit={form.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1 }}>
            <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
              Edit Live Class
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={form.values.name}
                label="Name"
                name="name"
                onChange={form.handleChange}
                error={form.touched.name && !!form.errors.name}
              />
              <TextField
                fullWidth
                variant="standard"
                value={form.values.meetingUrl}
                label="Meeting URL"
                name="meetingUrl"
                onChange={form.handleChange}
                error={form.touched.meetingUrl && !!form.errors.meetingUrl}
              />
              <TextField
                fullWidth
                variant="standard"
                value={form.values.quizPasscode}
                label="Quiz Passcode"
                name="quizPasscode"
                onChange={form.handleChange}
                error={form.touched.quizPasscode && !!form.errors.quizPasscode}
              />
              <TextField
                fullWidth
                variant="standard"
                value={form.values.code}
                label="Course Code"
                name="code"
                onChange={form.handleChange}
                error={form.touched.code && !!form.errors.code}
              />
              <TextField
                fullWidth
                variant="standard"
                value={form.values.hours}
                label="Hours"
                name="hours"
                onChange={form.handleChange}
                error={form.touched.hours && !!form.errors.hours}
              />
              <TextField
                fullWidth
                variant="standard"
                value={form.values.section}
                label="Section"
                name="section"
                onChange={form.handleChange}
                error={form.touched.section && !!form.errors.section}
              />
              <FormControl fullWidth>
                <InputLabel sx={{ marginLeft: -2 }}>Template</InputLabel>
                <Select
                  variant="standard"
                  value={form.values.templateID}
                  label="Template"
                  name="templateID"
                  onChange={form.handleChange}
                  error={form.touched.templateID && !!form.errors.templateID}
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <label style={{ marginTop: 0 }}>
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<ImageIcon />}
                  >
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      type="file"
                      onChange={(e) => handleCapture(e)}
                    />
                    {!existingFile ? 'Select' : 'Overwrite'} Thumbnail
                  </Button>
                </label>
                {fileToUpload && (
                  <Typography variant="subtitle1">
                    <UploadFileIcon
                      color="primary"
                      sx={{ mr: '2px', mb: '2px' }}
                    />
                    {fileToUpload.name}
                  </Typography>
                )}
                {existingFile && (
                  <Typography color="text.secondary" variant="subtitle2">
                    *The existing thumbnail file will only be overwritten upon
                    submitting this form with a new image uploaded.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              {saving ? (
                <CircularProgress size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                    disabled={!existingFile && !fileToUpload}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setFormOpen('')}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          <strong>Error with request: </strong>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
